import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Sort, type SortCallbackData, type SortOption } from '@shared/components/ui/Sort';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { useClientRuleConditionValueSelect } from '@modules/configuration/Rules/hooks/useClientRuleConditionValueSelect';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxGroupsSelector } from '../display/BoxGroupsSelector';
import {
  BoxExceptionModel,
  BoxGroupModel,
  CreateBoxExceptionConditionPayload,
  CreateBoxExceptionPayload,
  PackagingRuleField,
  PackagingRuleOperator,
} from '../../models/PackagingRulesets';
import { ExceptionConditionValueField } from './ExceptionConditionValueField';
import {
  ConditionFormRow,
  buildFinalConditions,
  conditionToRow,
  createConditionRow,
  getDefaultOperatorForField,
  isBooleanField,
  isConditionRowValid,
  isIdField,
  NUMERIC_COMPARISON_FIELDS,
} from './exceptionConditionConfig';

export interface EditExceptionSubmitPayload {
  originalExceptionId: number;
  exception: CreateBoxExceptionPayload;
  conditions: CreateBoxExceptionConditionPayload[];
}

export interface EditExceptionModalRef {
  open: (exception: BoxExceptionModel) => void;
  close: () => void;
}

interface EditExceptionModalProps {
  teamId?: number;
  boxGroups: BoxGroupModel[];
  countries: { id: number; name: string; iso2: string }[];
  boxGroupsSearchValue: string;
  onBoxGroupsSearchChange: (value: string) => void;
  boxGroupsSortValue: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc';
  onBoxGroupsSortChange: (value: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => void;
  hasBoxGroupsSortSelection: boolean;
  hasMoreBoxGroups?: boolean;
  isFetchingMoreBoxGroups?: boolean;
  isBoxGroupsLoading?: boolean;
  onLoadMoreBoxGroups?: () => void;
  isSubmitting?: boolean;
  onSubmit: (payload: EditExceptionSubmitPayload) => Promise<void> | void;
}

export const EditExceptionModal = forwardRef<EditExceptionModalRef, EditExceptionModalProps>(
  (
    {
      teamId,
      boxGroups,
      countries,
      boxGroupsSearchValue,
      onBoxGroupsSearchChange,
      boxGroupsSortValue,
      onBoxGroupsSortChange,
      hasBoxGroupsSortSelection,
      hasMoreBoxGroups = false,
      isFetchingMoreBoxGroups = false,
      isBoxGroupsLoading = false,
      onLoadMoreBoxGroups,
      isSubmitting,
      onSubmit,
    },
    ref,
  ) => {
    const { t } = useTranslation('packagingRulesetsModule');
    const modalRef = useRef<ModalCenterRef>(null);
    const productSelect = useClientRuleConditionValueSelect(teamId, 'product_id');
    const packSelect = useClientRuleConditionValueSelect(teamId, 'pack_id');

    const [originalExceptionId, setOriginalExceptionId] = useState<number | null>(null);
    const [name, setName] = useState('');
    const [active, setActive] = useState(true);
    const [priority, setPriority] = useState(100);
    const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
    const [conditionRows, setConditionRows] = useState<ConditionFormRow[]>([createConditionRow()]);

    useImperativeHandle(ref, () => ({
      open: (exception: BoxExceptionModel) => {
        setOriginalExceptionId(exception.id);
        setName(exception.name);
        setActive(exception.active);
        setPriority(exception.priority);
        setSelectedGroupId(exception.box_group_id ?? null);

        const apiConditions = (exception.conditions ?? []).map((condition) => ({
          field: condition.field as PackagingRuleField,
          operator: condition.operator,
          value_type: condition.value_type,
          value_string: condition.value_string ?? undefined,
          value_number: condition.value_number ?? undefined,
          value_boolean: condition.value_boolean ?? undefined,
          group_no: condition.group_no ?? 0,
        }));

        if (apiConditions.length > 0) {
          let prevGroupNo: number | null = null;
          const rows = apiConditions.map((condition) => {
            const row = conditionToRow(condition, prevGroupNo);
            prevGroupNo = condition.group_no ?? 0;
            return row;
          });
          setConditionRows(rows);
        } else {
          setConditionRows([createConditionRow()]);
        }

        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const updateConditionRow = (id: string, updater: (row: ConditionFormRow) => ConditionFormRow) => {
      setConditionRows((prev) => prev.map((row) => (row.id === id ? updater(row) : row)));
    };

    const handleFieldChange = (id: string, newField: string) => {
      updateConditionRow(id, (row) => ({
        ...row,
        field: newField as PackagingRuleField,
        value: '',
        boolValue: false,
        operator: getDefaultOperatorForField(newField as PackagingRuleField),
      }));
    };

    const fieldOptions = [
      { value: 'campaign', label: t('specialRulesModal.fields.special_campaign') },
      { value: 'total_amount', label: t('specialRulesModal.fields.total_amount') },
      { value: 'fragile', label: t('specialRulesModal.fields.fragile') },
      { value: 'country', label: t('specialRulesModal.fields.country') },
      { value: 'product_id', label: t('specialRulesModal.fields.product_id') },
      { value: 'pack_id', label: t('specialRulesModal.fields.pack_id') },
    ];

    const idOperatorOptions = [
      { value: '=', label: t('specialRulesModal.operators.includes') },
      { value: '!=', label: t('specialRulesModal.operators.notIncludes') },
    ];

    const numericOperatorOptions = [
      { value: '>', label: t('specialRulesModal.operators.gt') },
      { value: '<', label: t('specialRulesModal.operators.lt') },
      { value: '>=', label: t('specialRulesModal.operators.gte') },
      { value: '<=', label: t('specialRulesModal.operators.lte') },
      { value: '=', label: t('specialRulesModal.operators.eq') },
      { value: '!=', label: t('specialRulesModal.operators.neq') },
    ];

    const stringOperatorOptions = [
      { value: '=', label: t('specialRulesModal.operators.eq') },
      { value: '!=', label: t('specialRulesModal.operators.neq') },
      { value: 'contains', label: t('specialRulesModal.operators.contains') },
    ];

    const getOperatorOptions = (row: ConditionFormRow) => {
      if (isIdField(row)) return idOperatorOptions;
      if (isBooleanField(row)) return stringOperatorOptions.filter((o) => ['=', '!='].includes(o.value));
      if (NUMERIC_COMPARISON_FIELDS.includes(row.field)) return numericOperatorOptions;
      return stringOperatorOptions;
    };

    const boxGroupsSortOptions = useMemo(
      (): SortOption[] => [
        { field: 'id', direction: 'desc', label: 'Mas nuevo a mas viejo' },
        { field: 'id', direction: 'asc', label: 'Mas viejo a mas nuevo' },
        { field: 'name', direction: 'asc', label: 'Alfabetico A-Z' },
        { field: 'name', direction: 'desc', label: 'Alfabetico Z-A' },
      ],
      [],
    );

    const parseSortValue = (sort: 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc') => {
      if (sort === 'id_asc') return { field: 'id', direction: 'asc' as const };
      if (sort === 'name_asc') return { field: 'name', direction: 'asc' as const };
      if (sort === 'name_desc') return { field: 'name', direction: 'desc' as const };
      return { field: 'id', direction: 'desc' as const };
    };

    const toSortValue = (f: string, direction: 'asc' | 'desc'): 'id_desc' | 'id_asc' | 'name_asc' | 'name_desc' => {
      if (f === 'id' && direction === 'asc') return 'id_asc';
      if (f === 'name' && direction === 'asc') return 'name_asc';
      if (f === 'name' && direction === 'desc') return 'name_desc';
      return 'id_desc';
    };

    const addConditionRow = () => {
      setConditionRows((prev) => [...prev, createConditionRow()]);
    };

    const removeCondition = (id: string) => {
      setConditionRows((prev) => (prev.length > 1 ? prev.filter((row) => row.id !== id) : prev));
    };

    const canSubmit =
      Boolean(originalExceptionId) &&
      name.trim().length > 0 &&
      selectedGroupId !== null &&
      conditionRows.length > 0 &&
      conditionRows.every(isConditionRowValid);

    const handleSave = async () => {
      if (!canSubmit || !selectedGroupId || !originalExceptionId) return;

      await onSubmit({
        originalExceptionId,
        exception: {
          name: name.trim(),
          box_group_id: selectedGroupId,
          priority,
          active,
        },
        conditions: buildFinalConditions(conditionRows),
      });

      modalRef.current?.closeModal();
    };

    return (
      <ModalCenter
        ref={modalRef}
        title={t('boxes.specialRules.editModal.title')}
        type="default"
        width="lg"
        data-testid="edit-box-exception-modal"
      >
        <div className="flex h-[78vh] max-h-[78vh] flex-col">
          <div className="space-y-4 overflow-y-auto pr-1 flex-1" data-testid="edit-box-exception-modal-content">
            <div className="space-y-1.5">
              <label className="text-sm text-text">{t('specialRulesModal.name')}</label>
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('specialRulesModal.namePlaceholder')}
                data-testid="edit-exception-name-input"
              />
            </div>

            <div className="flex items-start justify-between gap-3 rounded-md border border-card-border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text">{t('boxes.specialRules.editModal.active')}</p>
                <p className="text-xs text-subtext mt-0.5">{t('boxes.specialRules.editModal.activeHint')}</p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} data-testid="edit-exception-active-switch" />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-text">{t('boxes.specialRules.editModal.priority')}</label>
              <Input
                type="number"
                value={priority}
                onChange={(event) => setPriority(Number(event.target.value))}
                data-testid="edit-exception-priority-input"
              />
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-text font-medium">{t('specialRulesModal.condition')}</p>
              </div>

              {conditionRows.map((row, idx) => (
                <div key={row.id} className="space-y-2 rounded-md border border-card-border p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-subtext">Condición {idx + 1}</p>
                    {conditionRows.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-subtext hover:text-error shrink-0"
                        onClick={() => removeCondition(row.id)}
                        aria-label={t('specialRulesModal.removeConditionAria')}
                        data-testid={`edit-exception-remove-condition-${idx}`}
                      >
                        <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>

                  {idx > 0 && (
                    <Combobox
                      options={[
                        { value: 'AND', label: t('specialRulesModal.separatorAnd') },
                        { value: 'OR', label: t('specialRulesModal.separatorOr') },
                      ]}
                      value={row.joinWithPrevious}
                      setValue={(v) =>
                        updateConditionRow(row.id, (current) => ({
                          ...current,
                          joinWithPrevious: v as 'AND' | 'OR',
                        }))
                      }
                      placeholder={t('specialRulesModal.separatorAnd')}
                      dataTestId={`edit-exception-join-${idx}`}
                    />
                  )}

                  <Combobox
                    options={fieldOptions}
                    value={row.field}
                    setValue={(newField) => handleFieldChange(row.id, newField)}
                    placeholder={t('specialRulesModal.fieldPlaceholder')}
                    dataTestId={`edit-exception-field-combobox-${idx}`}
                  />

                  {isIdField(row) && (
                    <p className="text-xs text-subtext">{t(`specialRulesModal.fieldHints.${row.field}`)}</p>
                  )}

                  {isBooleanField(row) ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Combobox
                        options={getOperatorOptions(row)}
                        value={row.operator}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            operator: v as PackagingRuleOperator,
                          }))
                        }
                        placeholder={t('specialRulesModal.operatorPlaceholder')}
                        dataTestId={`edit-exception-operator-combobox-${idx}`}
                      />
                      <Combobox
                        options={[
                          { value: 'true', label: t('specialRulesModal.boolean.true') },
                          { value: 'false', label: t('specialRulesModal.boolean.false') },
                        ]}
                        value={row.boolValue ? 'true' : 'false'}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            boolValue: v === 'true',
                          }))
                        }
                        placeholder={t('specialRulesModal.valuePlaceholder')}
                        dataTestId={`edit-exception-value-boolean-${idx}`}
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Combobox
                        options={getOperatorOptions(row)}
                        value={row.operator}
                        setValue={(v) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            operator: v as PackagingRuleOperator,
                          }))
                        }
                        placeholder={t('specialRulesModal.operatorPlaceholder')}
                        dataTestId={`edit-exception-operator-combobox-${idx}`}
                      />
                      <ExceptionConditionValueField
                        row={row}
                        idx={idx}
                        countries={countries}
                        productSelect={productSelect}
                        packSelect={packSelect}
                        onValueChange={(value) =>
                          updateConditionRow(row.id, (current) => ({
                            ...current,
                            value,
                          }))
                        }
                        t={t}
                        testIdPrefix="edit-exception"
                      />
                    </div>
                  )}
                </div>
              ))}

              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addConditionRow}
                  data-testid="edit-exception-add-condition-button"
                >
                  {t('specialRulesModal.addCondition')}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm text-text">{t('specialRulesModal.boxGroup')}</label>
              <BoxGroupsSelector
                groups={boxGroups}
                selectedGroupId={selectedGroupId}
                onSelectGroup={(group) => setSelectedGroupId(group.id)}
                isLoading={isBoxGroupsLoading}
                emptyText={t('specialRulesModal.noBoxGroups')}
                searchValue={boxGroupsSearchValue}
                onSearchChange={onBoxGroupsSearchChange}
                searchPlaceholder={t('boxes.defaultGroup.searchPlaceholder')}
                searchTestId="edit-exception-box-groups-search-input"
                sortSlot={
                  <div className="shrink-0">
                    <Sort
                      options={boxGroupsSortOptions}
                      onSort={(data: SortCallbackData) =>
                        onBoxGroupsSortChange(toSortValue(data.field, data.direction))
                      }
                      initialValue={hasBoxGroupsSortSelection ? parseSortValue(boxGroupsSortValue) : undefined}
                      defaultLabel="Ordenar"
                      dataTestId="edit-exception-box-groups-sort"
                      size="default"
                      className="h-10 w-full justify-between"
                    />
                  </div>
                }
                testIdPrefix="edit-exception-group"
                emptyTestId="edit-exception-group-empty"
                hasMore={hasMoreBoxGroups}
                isFetchingMore={isFetchingMoreBoxGroups}
                onLoadMore={onLoadMoreBoxGroups}
                loadMoreTestId="edit-exception-group-load-more"
              />
            </div>
          </div>

          <div className="mt-3 flex justify-end gap-2 border-t border-card-border pt-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => modalRef.current?.closeModal()}
              data-testid="edit-exception-cancel-button"
            >
              {t('boxes.specialRules.editModal.cancel')}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSave}
              disabled={!canSubmit || Boolean(isSubmitting)}
              data-testid="edit-exception-save-button"
            >
              {isSubmitting ? t('boxes.specialRules.editModal.saving') : t('boxes.specialRules.editModal.save')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

EditExceptionModal.displayName = 'EditExceptionModal';
